import { useCallback, useState } from "react";
import { useCalculateTaxMutation } from "store/apis/taxCalculator";
import { useUpdatePhysicalNexusForStateMutation } from "store/apis/salesTax";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { toTaxCalculatorAddress } from "util/toTaxCalculatorAddress";
import { APPROACHING, EXPOSED, MONITORING } from "constants/nexusTrackerStatus";
import {
  REGISTERED as NEXUS_REGISTRATION_STATUS_REGISTERED,
  VALIDATING as NEXUS_REGISTRATION_STATUS_VALIDATING,
  IN_REVIEW,
  PROCESSING,
} from "constants/salesTaxRegistrationStatusType";
import { CART_ITEM_STATUS } from "constants/cart";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";

export type SalesTaxCode = {
  uuid: string;
  tax_code: string;
};

type LineItemAddress = {
  street_address: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
};

export type NexusComplianceInfo = {
  status: string;
  registrationStatus: string | null | undefined;
  stateName: string;
  stateId: string | null | undefined;
  registrationId: string | null | undefined;
  cartStatus: string | null | undefined;
  physicalPresenceConfirmedAbsent: boolean | null | undefined;
  launchedTaskId: string | null | undefined;
};

export const useLineItemTaxCompliance = ({
  entityId,
  sellerAddress,
  buyerAddress,
  systemTaxRate,
  onSystemTaxRateChange,
  initialSalesTaxCode,
}: {
  entityId: string;
  sellerAddress?: LineItemAddress;
  buyerAddress?: LineItemAddress;
  systemTaxRate: number | null | undefined;
  onSystemTaxRateChange: (rate: number | null) => void;
  initialSalesTaxCode: SalesTaxCode | null;
}) => {
  const { alertToast } = useToast();
  const { data: countriesData } = useGetCountriesDataQuery();
  const [calculateTax] = useCalculateTaxMutation();
  const [updatePhysicalNexusForState] =
    useUpdatePhysicalNexusForStateMutation();

  const [nexusComplianceInfo, setNexusComplianceInfo] =
    useState<NexusComplianceInfo | null>(null);
  const [isApproachingBannerDismissed, setIsApproachingBannerDismissed] =
    useState(false);
  const [selectedSalesTaxCode, setSelectedSalesTaxCode] =
    useState<SalesTaxCode | null>(initialSalesTaxCode);

  const calculateSystemTaxRate = useCallback(
    async (salesTaxCode: SalesTaxCode | null) => {
      if (!salesTaxCode) {
        onSystemTaxRateChange(null);
        return;
      }

      const seller = toTaxCalculatorAddress(
        sellerAddress || null,
        countriesData
      );
      const buyer = toTaxCalculatorAddress(buyerAddress || null, countriesData);

      if (!seller || !buyer) {
        onSystemTaxRateChange(null);
        return;
      }

      try {
        const response = await calculateTax({
          entityId,
          payload: {
            seller_address: seller,
            buyer_address: buyer,
            product_detail: {
              uuid: salesTaxCode.uuid,
              tax_code: salesTaxCode.tax_code,
            },
          },
        }).unwrap();
        setIsApproachingBannerDismissed(false);
        setNexusComplianceInfo({
          status: response.nexus_status,
          registrationStatus: response.nexus_registration_status,
          stateName: response.jurisdiction?.state_name,
          stateId: response.nexus_state_id,
          registrationId: response.nexus_registration_id,
          cartStatus: response.nexus_cart_status,
          physicalPresenceConfirmedAbsent:
            response.nexus_physical_presence_confirmed_absent,
          launchedTaskId: null,
        });

        const isRegisteredInState =
          response.nexus_registration_status ===
          NEXUS_REGISTRATION_STATUS_REGISTERED;

        onSystemTaxRateChange(
          isRegisteredInState ? response.total_tax_rate : null
        );
      } catch (error) {
        onSystemTaxRateChange(null);
        setNexusComplianceInfo(null);
      }
    },
    [
      sellerAddress,
      buyerAddress,
      countriesData,
      calculateTax,
      entityId,
      onSystemTaxRateChange,
    ]
  );

  const selectSalesTaxCode = useCallback(
    (salesTaxCode: SalesTaxCode | null) => {
      setSelectedSalesTaxCode(salesTaxCode);
      void calculateSystemTaxRate(salesTaxCode);
    },
    [calculateSystemTaxRate]
  );

  const confirmNoPhysicalPresence = useCallback(async () => {
    const stateId = nexusComplianceInfo?.stateId;
    if (!stateId) return;

    try {
      await updatePhysicalNexusForState({
        entityId,
        stateId,
        payload: {
          office_present: false,
          warehouse_present: false,
          employees_present: false,
        },
      }).unwrap();
      void calculateSystemTaxRate(selectedSalesTaxCode);
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  }, [
    nexusComplianceInfo?.stateId,
    updatePhysicalNexusForState,
    entityId,
    calculateSystemTaxRate,
    selectedSalesTaxCode,
    alertToast,
  ]);

  const recalculate = useCallback(
    () => calculateSystemTaxRate(selectedSalesTaxCode),
    [calculateSystemTaxRate, selectedSalesTaxCode]
  );

  const hasSellerAddress = Boolean(
    toTaxCalculatorAddress(sellerAddress || null, countriesData)
  );
  const hasBuyerAddress = Boolean(
    toTaxCalculatorAddress(buyerAddress || null, countriesData)
  );

  const needsComplianceAttention =
    nexusComplianceInfo?.status === EXPOSED ||
    nexusComplianceInfo?.status === APPROACHING;
  const isRegistrationInProgress =
    needsComplianceAttention &&
    (nexusComplianceInfo?.registrationStatus === IN_REVIEW ||
      nexusComplianceInfo?.registrationStatus === PROCESSING);
  const isPendingInCart =
    needsComplianceAttention &&
    nexusComplianceInfo?.registrationStatus ===
      NEXUS_REGISTRATION_STATUS_VALIDATING &&
    nexusComplianceInfo?.cartStatus === CART_ITEM_STATUS.IN_CART;
  const needsComplianceBanner =
    needsComplianceAttention &&
    !isRegistrationInProgress &&
    !isPendingInCart &&
    nexusComplianceInfo?.registrationStatus !==
      NEXUS_REGISTRATION_STATUS_REGISTERED;

  const showNoTaxRequiredBanner =
    !nexusComplianceInfo?.physicalPresenceConfirmedAbsent &&
    nexusComplianceInfo?.status === MONITORING &&
    (systemTaxRate === null || systemTaxRate === undefined);

  // Registration being incomplete (rather than a missing address) is the real
  // reason tax couldn't auto-calculate — the registration banners already
  // cover that case, so the address-missing message shouldn't also show.
  const isRegistrationIncomplete =
    needsComplianceAttention &&
    nexusComplianceInfo?.registrationStatus !==
      NEXUS_REGISTRATION_STATUS_REGISTERED;

  return {
    nexusComplianceInfo,
    selectedSalesTaxCode,
    selectSalesTaxCode,
    calculateSystemTaxRate,
    confirmNoPhysicalPresence,
    recalculate,
    hasSellerAddress,
    hasBuyerAddress,
    isRegistrationIncomplete,
    showExposedComplianceBanner:
      needsComplianceBanner && nexusComplianceInfo?.status === EXPOSED,
    showApproachingComplianceBanner:
      !isApproachingBannerDismissed &&
      needsComplianceBanner &&
      nexusComplianceInfo?.status === APPROACHING,
    showCartPendingBanner: isPendingInCart,
    showPendingRegistrationBanner: isRegistrationInProgress,
    showNoTaxRequiredBanner,
    dismissApproachingBanner: () => setIsApproachingBannerDismissed(true),
  };
};
